import {
  PendingJointAcatTransferStatus,
  useGetPendingJointAcatTransfers,
  useWealthOverview,
  type PendingJointAcatTransfer,
} from '@egw/api'
import { ActionTile, Alert, AlertType, Button, ButtonVariant, PageHeader } from '@egw/components'
import { FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { useFlags } from '@egw/feature-flags'
import { useTranslation } from '@egw/i18n'
import { type FC } from 'react'
import { useNavigate } from 'react-router'

export const TransferLandingPage: FC = () => {
  const { t } = useTranslation('features-transfer')
  const navigate = useNavigate()

  const { flags } = useFlags<FeatureFlags>()

  const { data, error } = useGetPendingJointAcatTransfers({ refetchOnMount: 'always' }) // Refetches on mount because we want to get latest ACAT transfers for the user that other Joint User may have submitted

  const { data: wealthOverview } = useWealthOverview()

  const getAccountNickname = (alias: number): string | undefined => {
    return wealthOverview?.personalPortfolio?.accounts.find((account) => account.alias === alias)?.accountName
  }

  const pendingAcatTransfers: PendingJointAcatTransfer[] = (() => {
    if (error ?? !data?.pendingJointAcatTransfers) {
      return []
    }

    return data.pendingJointAcatTransfers.filter(
      (transfer: PendingJointAcatTransfer) => transfer.status === PendingJointAcatTransferStatus.REQUIRES_REVIEW,
    )
  })()

  const fundingOptionHandler = (path: string): void => {
    void navigate(path)
  }

  return (
    <>
      <PageHeader title={t('transfer_landing_page.page_title')} />
      <div className="egw-container">
        <div className="flex flex-col gap-xs mb-2xl">
          {wealthOverview && pendingAcatTransfers.length > 0
            ? pendingAcatTransfers.map(({ jointAccountHolderFirstName, toAccountAlias, pendingAcatTransferAlias }) => (
                <Alert
                  key={`pending-acat-${pendingAcatTransferAlias.toString()}`}
                  showIcon={false}
                  title={t('transfer_landing_page.pending_review.title')}
                  description={t('transfer_landing_page.pending_review.description', {
                    jointOwnerName: jointAccountHolderFirstName,
                    accountNickname: getAccountNickname(toAccountAlias),
                  })}
                  alertType={AlertType.Info}
                >
                  <Button
                    variant={ButtonVariant.Primary}
                    className="w-fit typo-primary-sm-bold mt-sm"
                    size="small"
                    onClick={() => {
                      void navigate(
                        WEALTH_ROUTES.TRANSFER_ACCOUNT_REVIEW.path.replace(
                          ':transferAlias',
                          pendingAcatTransferAlias.toString(),
                        ),
                      )
                    }}
                  >
                    {t('transfer_landing_page.pending_review.submit')}
                  </Button>
                </Alert>
              ))
            : null}
        </div>
        <div className="flex flex-col gap-sm w-full">
          <ActionTile
            testId="bank_transfer_link"
            title={t('transfer_landing_page.deposit_or_withdrawal_title')}
            description={t('transfer_landing_page.bank_transfer_description')}
            onClick={() => {
              fundingOptionHandler(WEALTH_ROUTES.TRANSFER_BANK.path)
            }}
          />
          <ActionTile
            testId="account_transfer_link"
            title={t('transfer_landing_page.account_transfer_title')}
            description={t('transfer_landing_page.account_transfer_description')}
            onClick={() => {
              fundingOptionHandler(WEALTH_ROUTES.TRANSFER_ACCOUNT.path)
            }}
          />
          <ActionTile
            testId="wire_transfer_link"
            title={t('transfer_landing_page.wire_transfer_title')}
            description={t('transfer_landing_page.wire_transfer_description')}
            onClick={() => {
              fundingOptionHandler(WEALTH_ROUTES.TRANSFER_WIRE.path)
            }}
          />
          <ActionTile
            testId="ira_rollover_transfer_link"
            title={t('transfer_landing_page.ira_rollover_title')}
            description={t('transfer_landing_page.ira_rollover_description')}
            onClick={() => {
              fundingOptionHandler(WEALTH_ROUTES.TRANSFER_ROLLOVER_IRA.path)
            }}
          />
        </div>
      </div>
    </>
  )
}
